/**
 * Input sampling: keyboard and gamepad -> `CarInput`.
 *
 * Input is sampled ONCE per physics tick, not per rendered frame. Sampling per
 * frame would make control resolution depend on refresh rate, which is exactly
 * the class of frame-rate dependence the fixed timestep exists to remove.
 *
 * Analog axes are passed through with a deadzone but are otherwise not
 * smoothed or shaped: Rocket League's own curves live in the physics layer
 * (steering curvature, the aerial damping cutoff at |input| = 1), and shaping
 * here would corrupt them. In particular the aerial model behaves discretely
 * differently at exactly +/-1, so a controller at full deflection must produce
 * exactly 1.0 — hence the outer saturation zone below.
 *
 * ===========================================================================
 * THREE DEVICES, ONE PRIORITY ORDER
 * ===========================================================================
 *
 *   pad      whenever one is connected. A player who plugged in a controller
 *            reached for it.
 *   touch    whenever the on-screen controls are MOUNTED, which is a question
 *            ../ui and `touchControlsVisible` answer, not this file.
 *   keyboard otherwise.
 *
 * Touch outranks the keyboard on a mounted overlay rather than only while a
 * finger is down, and that is deliberate: with auto-throttle the neutral touch
 * state means "drive", so a fall-through to an idle keyboard would stop the
 * car every time the player let go. The overlay is only mounted on a device
 * whose primary pointer is a finger, or because the player asked for it — see
 * `touchControlsVisible` in ./touch-model.ts for why that test is stricter
 * than "has a touchscreen".
 */

import type { CarInput } from '../physics/car-state';
import { clamp } from '../physics/math';
import { touchCarInput, touchEngaged, type TouchState } from './touch-model';

/** Below this the stick reads as zero. Standard for drift-prone sticks. */
const STICK_DEADZONE = 0.12;
/**
 * Above this the stick reads as exactly +/-1. Without it a worn stick that
 * tops out at 0.97 would never turn off aerial damping, and the car would feel
 * sluggish in the air for reasons the player cannot see.
 */
const STICK_SATURATION = 0.95;

/** Trigger travel below this counts as released. */
const TRIGGER_THRESHOLD = 0.15;

/**
 * The page's `window`, or null under Node.
 *
 * Blur and the gamepad connection are WINDOW events specifically — a caller
 * that passes its own `target` for the keyboard still wants to hear about a
 * pad, and a tab losing focus is not something a `<canvas>` reports. Reaching
 * for the global directly is what that costs, and the null case is what keeps
 * this class constructible in Node, where the RL environment and the tests
 * both build one.
 */
const globalWindow: Window | null = typeof window === 'undefined' ? null : window;

const applyStickCurve = (raw: number): number => {
  const magnitude = Math.abs(raw);
  if (magnitude < STICK_DEADZONE) return 0;
  if (magnitude > STICK_SATURATION) return Math.sign(raw);
  // Rescale the live band to [0, 1] so the response is continuous at both ends.
  const scaled = (magnitude - STICK_DEADZONE) / (STICK_SATURATION - STICK_DEADZONE);
  return Math.sign(raw) * scaled;
};

export interface KeyBindings {
  throttleForward: string[];
  throttleReverse: string[];
  steerLeft: string[];
  steerRight: string[];
  pitchUp: string[];
  pitchDown: string[];
  yawLeft: string[];
  yawRight: string[];
  rollLeft: string[];
  rollRight: string[];
  jump: string[];
  boost: string[];
  handbrake: string[];
  ballCam: string[];
}

export const DEFAULT_BINDINGS: KeyBindings = {
  throttleForward: ['KeyW', 'ArrowUp'],
  throttleReverse: ['KeyS', 'ArrowDown'],
  steerLeft: ['KeyA', 'ArrowLeft'],
  steerRight: ['KeyD', 'ArrowRight'],
  // In the air the same keys pitch and yaw; roll is the modifier-held variant.
  pitchUp: ['KeyS', 'ArrowDown'],
  pitchDown: ['KeyW', 'ArrowUp'],
  yawLeft: ['KeyA', 'ArrowLeft'],
  yawRight: ['KeyD', 'ArrowRight'],
  rollLeft: ['KeyQ'],
  rollRight: ['KeyE'],
  jump: ['Space'],
  boost: ['ShiftLeft', 'ShiftRight'],
  handbrake: ['KeyC', 'ControlLeft'],
  ballCam: ['KeyV'],
};

/**
 * The on-screen controls, as this file needs them. `TouchControls` in
 * ./touch.ts satisfies it; a test satisfies it with an object literal.
 *
 * An interface rather than the class, because the class builds a stylesheet
 * and a tree of divs in its constructor and this file must remain usable in
 * Node — the RL environment and the headless sim both import it.
 */
export interface TouchSource {
  /** False while the overlay is hidden (paused, or not a touch device). */
  readonly visible: boolean;
  /** Live state; see the note on `TouchControls.read`. */
  read(): TouchState;
  /** Whether the car drives with nothing held. */
  readonly driveOnItsOwn: boolean;
}

export class InputController {
  private readonly pressed = new Set<string>();
  private gamepadIndex: number | null = null;
  private touch: TouchSource | null = null;

  /** Toggled state, not a per-tick input, so it lives here rather than in CarInput. */
  ballCam = true;
  private ballCamLatch = false;

  constructor(
    private readonly bindings: KeyBindings = DEFAULT_BINDINGS,
    target: EventTarget = globalWindow ?? new EventTarget()
  ) {
    target.addEventListener('keydown', e => {
      const ev = e as KeyboardEvent;
      // Prevent the page scrolling out from under the game on space/arrows.
      if (ev.code === 'Space' || ev.code.startsWith('Arrow')) ev.preventDefault();
      this.pressed.add(ev.code);
    });
    target.addEventListener('keyup', e => this.pressed.delete((e as KeyboardEvent).code));
    // A lost focus that leaves keys stuck down is the most common cause of
    // "my car drives off on its own" in browser games.
    globalWindow?.addEventListener('blur', () => this.pressed.clear());

    globalWindow?.addEventListener('gamepadconnected', e => {
      this.gamepadIndex = (e as GamepadEvent).gamepad.index;
    });
    globalWindow?.addEventListener('gamepaddisconnected', e => {
      if (this.gamepadIndex === (e as GamepadEvent).gamepad.index) this.gamepadIndex = null;
    });
  }

  /** Attach (or, with null, detach) the on-screen controls. */
  setTouchSource(source: TouchSource | null): void {
    this.touch = source;
  }

  /** The touch state to obey this tick, or null when touch is not driving. */
  private activeTouch(): TouchState | null {
    const touch = this.touch;
    if (touch === null || !touch.visible) return null;
    return touch.read();
  }

  private anyDown(codes: string[]): boolean {
    for (const c of codes) if (this.pressed.has(c)) return true;
    return false;
  }

  private axis(negative: string[], positive: string[]): number {
    return (this.anyDown(positive) ? 1 : 0) - (this.anyDown(negative) ? 1 : 0);
  }

  private readGamepad(): Gamepad | null {
    if (this.gamepadIndex === null || typeof navigator.getGamepads !== 'function') return null;
    return navigator.getGamepads()[this.gamepadIndex] ?? null;
  }

  /**
   * Sample the current input. `airborne` selects the air mapping for the
   * shared keys: on the ground WASD is throttle+steer, in the air it is
   * pitch+yaw (or pitch+roll while the air-roll modifier is held).
   */
  sample(airborne: boolean): CarInput {
    const pad = this.readGamepad();

    if (pad) {
      const lx = applyStickCurve(pad.axes[0] ?? 0);
      const ly = applyStickCurve(pad.axes[1] ?? 0);
      const rt = pad.buttons[7]?.value ?? 0;
      const lt = pad.buttons[6]?.value ?? 0;
      const airRoll = (pad.buttons[4]?.pressed ?? false) || (pad.buttons[10]?.pressed ?? false);

      this.updateBallCamLatch(pad.buttons[3]?.pressed ?? false);

      return {
        throttle: clamp(rt - lt, -1, 1),
        // Stick left is -X on a gamepad but +steer in our frame.
        steer: -lx,
        pitch: ly,
        yaw: airRoll ? 0 : -lx,
        roll: airRoll ? -lx : 0,
        jump: pad.buttons[0]?.pressed ?? false,
        boost: pad.buttons[1]?.pressed ?? false,
        handbrake: airRoll || (pad.buttons[2]?.pressed ?? false),
      };
    }

    const touch = this.activeTouch();
    if (touch !== null) {
      this.updateBallCamLatch(touch.buttons.ballCam);
      return touchCarInput(touch, airborne, this.touch!.driveOnItsOwn);
    }

    const b = this.bindings;
    this.updateBallCamLatch(this.anyDown(b.ballCam));

    const horizontal = this.axis(b.steerRight, b.steerLeft);
    const airRoll = this.anyDown(b.handbrake);
    const rollFromKeys = this.axis(b.rollRight, b.rollLeft);

    return {
      throttle: this.axis(b.throttleReverse, b.throttleForward),
      steer: horizontal,
      pitch: airborne ? this.axis(b.pitchDown, b.pitchUp) : 0,
      yaw: airborne && !airRoll ? horizontal : 0,
      roll: airborne ? (airRoll ? horizontal : rollFromKeys) : rollFromKeys,
      jump: this.anyDown(b.jump),
      boost: this.anyDown(b.boost),
      handbrake: airRoll,
    };
  }

  /** Edge-triggered so holding the key does not strobe the camera. */
  private updateBallCamLatch(down: boolean): void {
    if (down && !this.ballCamLatch) this.ballCam = !this.ballCam;
    this.ballCamLatch = down;
  }

  /** True while any trigger/button is meaningfully engaged — used for menus. */
  anyInput(): boolean {
    if (this.pressed.size > 0) return true;
    // A stab at the screen during a replay means "get on with it" whichever
    // button it landed on. Auto-throttle is NOT counted: the car driving on
    // its own is not the player asking for anything.
    const touch = this.activeTouch();
    if (touch !== null && touchEngaged(touch)) return true;
    const pad = this.readGamepad();
    if (!pad) return false;
    return pad.buttons.some(btn => btn.pressed || btn.value > TRIGGER_THRESHOLD);
  }
}
